//src/factory/symbols/createArrows.js
import * as THREE from 'three';
import { generateUniqueId } from '../../utils/generateUniqueId';

// Ok sembolü sabitleri
const ARROW_DEFAULTS = {
  circleRadius: 2.7,      // Daire yarıçapı
  lineLength: 25,         // Çizgi uzunluğu
  triangleBase: 5,        // Üçgen taban genişliği
  triangleHeight: 6       // Üçgen yüksekliği
};

/**
 * Üçgen ok başı (solid triangle arrowhead) oluşturur
 * İkizkenar üçgen - taban aşağıda, uç yukarıda
 * @param {number} base - Üçgen taban genişliği
 * @param {number} height - Üçgen yüksekliği
 * @param {number} color - Renk
 * @returns {THREE.Mesh} - Üçgen mesh
 */
function createTriangleArrowhead(base = ARROW_DEFAULTS.triangleBase, height = ARROW_DEFAULTS.triangleHeight, color = 0xffffff) {
  const shape = new THREE.Shape();
  // İkizkenar üçgen: taban altta, uç yukarıda
  const halfBase = base / 2;
  shape.moveTo(0, height);           // Üst nokta (uç)
  shape.lineTo(-halfBase, 0);        // Sol alt
  shape.lineTo(halfBase, 0);         // Sağ alt
  shape.closePath();

  const geometry = new THREE.ShapeGeometry(shape);
  const material = new THREE.MeshBasicMaterial({ 
    color: color, 
    side: THREE.DoubleSide 
  });
  
  return new THREE.Mesh(geometry, material);
}

/**
 * Dolu daire oluşturur
 * @param {number} radius - Daire yarıçapı
 * @param {number} color - Renk
 * @returns {THREE.Mesh} - Daire mesh
 */
function createFilledCircle(radius = ARROW_DEFAULTS.circleRadius, color = 0xffffff) {
  const geometry = new THREE.CircleGeometry(radius, 32);
  const material = new THREE.MeshBasicMaterial({ 
    color: color, 
    side: THREE.DoubleSide 
  });
  
  return new THREE.Mesh(geometry, material);
}

/**
 * Çizgi oluşturur
 * @param {THREE.Vector3} start - Başlangıç noktası
 * @param {THREE.Vector3} end - Bitiş noktası
 * @param {number} color - Renk
 * @returns {THREE.Line} - Çizgi
 */
function createLine(start, end, color = 0xffffff) {
  const geometry = new THREE.BufferGeometry().setFromPoints([start, end]);
  const material = new THREE.LineBasicMaterial({ color: color });
  
  return new THREE.Line(geometry, material);
}

/**
 * Ok sembolü oluşturur (daire + çizgi + üçgen ok başı)
 * Varsayılan değerler:
 * - Daire yarıçapı: 2.7 birim
 * - Çizgi uzunluğu: 25 birim (daire sınırından başlar)
 * - Üçgen: taban 5 birim, yükseklik 6 birim
 * 
 * @param {THREE.Scene|THREE.Group} target - Eklenecek hedef (scene veya group)
 * @param {Object} options - Seçenekler
 * @param {Object} options.position - Base point pozisyonu {x, y}
 * @param {number} options.rotation - Dönüş açısı (derece)
 * @param {number} options.color - Renk
 * @param {number} options.circleRadius - Daire yarıçapı (varsayılan: 2.7)
 * @param {number} options.lineLength - Çizgi uzunluğu (varsayılan: 25)
 * @param {number} options.triangleBase - Üçgen taban genişliği (varsayılan: 5)
 * @param {number} options.triangleHeight - Üçgen yüksekliği (varsayılan: 6)
 * @param {string} options.layerName - Layer adı
 * @returns {THREE.Group} - Ok grubu
 */
export function createArrow(target, options = {}) {
  const {
    position = { x: 0, y: 0 },
    rotation = 0,
    color = 0xffffff,
    circleRadius = ARROW_DEFAULTS.circleRadius,
    lineLength = ARROW_DEFAULTS.lineLength,
    triangleBase = ARROW_DEFAULTS.triangleBase,
    triangleHeight = ARROW_DEFAULTS.triangleHeight,
    layerName = '0',
    isPreview = false
  } = options;

  const group = new THREE.Group();
  const arrowId = generateUniqueId('arrow');

  // 1. Daire (base point'te, yarıçap: 2.7)
  const circle = createFilledCircle(circleRadius, color);
  circle.position.set(0, 0, 0);
  circle.userData = {
    role: 'circle',
    arrowId,
    originalColor: color
  };
  group.add(circle);

  // 2. Çizgi (daire sınırından SAĞA doğru 25 birim uzunluğunda)
  // X ekseni boyunca çiziliyor (0 derece = sağ)
  const lineStart = new THREE.Vector3(circleRadius, 0, 0);
  const lineEnd = new THREE.Vector3(circleRadius + lineLength, 0, 0);

  const line = createLine(lineStart, lineEnd, color);
  line.userData = {
    role: 'line',
    arrowId,
    originalColor: color
  };
  group.add(line);

  // 3. Üçgen ok başı (çizginin ucunda, taban: 5, yükseklik: 6)
  // Üçgen sağa bakacak şekilde döndürülmüş (-90 derece)
  const arrowhead = createTriangleArrowhead(triangleBase, triangleHeight, color);
  arrowhead.position.set(circleRadius + lineLength, 0, 0);
  arrowhead.rotation.z = -Math.PI / 2; // Sağa bakacak şekilde döndür
  arrowhead.userData = {
    role: 'arrowhead',
    arrowId,
    originalColor: color
  };
  group.add(arrowhead);

  // Grubu konumlandır ve döndür
  group.position.set(position.x, position.y, 0);
  group.rotation.z = rotation * Math.PI / 180; // Derece -> radyan

  group.userData = {
    id: arrowId,
    type: 'arrow',
    isSelectable: true,
    isPreview: isPreview,
    position: { x: position.x, y: position.y },
    rotation: rotation,
    color: color,
    circleRadius: circleRadius,
    lineLength: lineLength,
    triangleBase: triangleBase,
    triangleHeight: triangleHeight,
    layerName: layerName,
    originalColor: color
  };

  if (target?.add) {
    target.add(group);
  }

  return group;
}

/**
 * Sadece üçgen ok başı oluşturur
 * @param {THREE.Scene|THREE.Group} target - Eklenecek hedef
 * @param {Object} options - Seçenekler
 * @returns {THREE.Group} - Ok başı grubu
 */
export function createTriangleArrow(target, options = {}) {
  const {
    position = { x: 0, y: 0 },
    rotation = 0,
    base = ARROW_DEFAULTS.triangleBase,
    height = ARROW_DEFAULTS.triangleHeight,
    color = 0xffffff,
    layerName = '0',
    isPreview = false
  } = options;

  const group = new THREE.Group();
  const arrowId = generateUniqueId('triangleArrow');

  const arrowhead = createTriangleArrowhead(base, height, color);
  arrowhead.userData = { 
    role: 'arrowhead', 
    arrowId,
    originalColor: color 
  };
  group.add(arrowhead);

  group.position.set(position.x, position.y, 0);
  group.rotation.z = rotation * Math.PI / 180; // Derece -> radyan

  group.userData = {
    id: arrowId,
    type: 'triangleArrow',
    isSelectable: true,
    isPreview: isPreview,
    position: { x: position.x, y: position.y },
    rotation: rotation,
    base: base,
    height: height,
    color: color,
    layerName: layerName,
    originalColor: color
  };

  if (target?.add) {
    target.add(group);
  }

  return group;
}

/**
 * Sadece dolu daire oluşturur
 * @param {THREE.Scene|THREE.Group} target - Eklenecek hedef
 * @param {Object} options - Seçenekler
 * @returns {THREE.Group} - Daire grubu
 */
export function createCircleMarker(target, options = {}) {
  const {
    position = { x: 0, y: 0 },
    radius = ARROW_DEFAULTS.circleRadius,
    color = 0xffffff,
    layerName = '0',
    isPreview = false
  } = options;

  const group = new THREE.Group();
  const markerId = generateUniqueId('circleMarker');

  const circle = createFilledCircle(radius, color);
  circle.userData = { 
    role: 'circle', 
    markerId,
    originalColor: color 
  };
  group.add(circle);

  group.position.set(position.x, position.y, 0);

  group.userData = {
    id: markerId,
    type: 'circleMarker',
    isSelectable: true,
    isPreview: isPreview,
    position: { x: position.x, y: position.y },
    radius: radius,
    color: color,
    layerName: layerName,
    originalColor: color
  };

  if (target?.add) {
    target.add(group);
  }

  return group;
}

/**
 * Leader (kılavuz çizgisi) oluşturur - daire + çizgi(ler) + ok başı
 * @param {THREE.Scene|THREE.Group} target - Eklenecek hedef
 * @param {Object} options - Seçenekler
 * @returns {THREE.Group} - Leader grubu
 */
export function createLeader(target, options = {}) {
  const {
    points = [{ x: 0, y: 0 }, { x: 50, y: 50 }],
    color = 0xffffff,
    circleRadius = ARROW_DEFAULTS.circleRadius,
    triangleBase = ARROW_DEFAULTS.triangleBase,
    triangleHeight = ARROW_DEFAULTS.triangleHeight,
    showStartCircle = true,
    layerName = '0',
    isPreview = false
  } = options;

  if (points.length < 2) {
    console.warn('Leader için en az 2 nokta gerekli');
    return null;
  }

  const group = new THREE.Group();
  const leaderId = generateUniqueId('leader');

  // Başlangıç dairesi
  if (showStartCircle) {
    const circle = createFilledCircle(circleRadius, color);
    circle.position.set(points[0].x, points[0].y, 0);
    circle.userData = { 
      role: 'circle', 
      leaderId,
      originalColor: color 
    };
    group.add(circle);
  }

  // İlk çizgi başlangıç noktasını hesapla (daire sınırından)
  let firstLineStart = new THREE.Vector3(points[0].x, points[0].y, 0);
  if (showStartCircle && points.length >= 2) {
    const dir = new THREE.Vector2(
      points[1].x - points[0].x,
      points[1].y - points[0].y
    ).normalize();
    firstLineStart = new THREE.Vector3(
      points[0].x + dir.x * circleRadius,
      points[0].y + dir.y * circleRadius,
      0
    );
  }

  // Çizgiler (noktalar arası)
  for (let i = 0; i < points.length - 1; i++) {
    let start, end;
    
    if (i === 0) {
      start = firstLineStart;
    } else {
      start = new THREE.Vector3(points[i].x, points[i].y, 0);
    }
    
    // Son segment için ok başı hesaba katılmalı
    if (i === points.length - 2) {
      const dir = new THREE.Vector2(
        points[i + 1].x - points[i].x,
        points[i + 1].y - points[i].y
      ).normalize();
      end = new THREE.Vector3(
        points[i + 1].x - dir.x * triangleHeight,
        points[i + 1].y - dir.y * triangleHeight,
        0
      );
    } else {
      end = new THREE.Vector3(points[i + 1].x, points[i + 1].y, 0);
    }
    
    const line = createLine(start, end, color);
    line.userData = { 
      role: 'line', 
      leaderId,
      segmentIndex: i,
      originalColor: color 
    };
    group.add(line);
  }

  // Ok başı (son noktada)
  const lastPoint = points[points.length - 1];
  const prevPoint = points[points.length - 2];
  const direction = new THREE.Vector2(
    lastPoint.x - prevPoint.x,
    lastPoint.y - prevPoint.y
  );
  const angle = Math.atan2(direction.y, direction.x);

  const arrowhead = createTriangleArrowhead(triangleBase, triangleHeight, color);
  // Ok başını son noktaya konumlandır (üçgen tabanı bu noktada)
  arrowhead.position.set(
    lastPoint.x - Math.cos(angle) * triangleHeight,
    lastPoint.y - Math.sin(angle) * triangleHeight,
    0
  );
  arrowhead.rotation.z = angle - Math.PI / 2;
  arrowhead.userData = { 
    role: 'arrowhead', 
    leaderId,
    originalColor: color 
  };
  group.add(arrowhead);

  group.userData = {
    id: leaderId,
    type: 'leader',
    isSelectable: true,
    isPreview: isPreview,
    points: points,
    color: color,
    circleRadius: circleRadius,
    triangleBase: triangleBase,
    triangleHeight: triangleHeight,
    showStartCircle: showStartCircle,
    layerName: layerName,
    originalColor: color
  };

  if (target?.add) {
    target.add(group);
  }

  return group;
}

/**
 * Gelen (incoming) ok sembolü oluşturur - diğer kattan gelen hat için
 *
 * Yapı (soldan sağa):
 * - Daire (base point, mevcut katı gösterir)
 * - Üçgen ok başı (sivri ucu daireye dokunur - gelen yönü gösterir)
 * - Çizgi (ok başından dışarı doğru uzanır)
 *
 * Yukarıdan gelen (fromAbove): Ok yukarıdan aşağı doğru, sivri uç daireye temas
 * Aşağıdan gelen (fromBelow): Ok aşağıdan yukarı doğru, sivri uç daireye temas
 *
 * @param {THREE.Scene|THREE.Group} target - Eklenecek hedef
 * @param {Object} options - Seçenekler
 * @param {Object} options.position - Base point pozisyonu {x, y}
 * @param {number} options.color - Renk
 * @param {string} options.direction - 'fromAbove' (yukarıdan) veya 'fromBelow' (aşağıdan)
 * @returns {THREE.Group} - Ok grubu
 */
export function createIncomingArrow(target, options = {}) {
  const {
    position = { x: 0, y: 0 },
    rotation = 0,
    color = 0xffffff,
    circleRadius = ARROW_DEFAULTS.circleRadius,
    lineLength = ARROW_DEFAULTS.lineLength,
    triangleBase = ARROW_DEFAULTS.triangleBase,
    triangleHeight = ARROW_DEFAULTS.triangleHeight,
    layerName = '0',
    isPreview = false,
    direction = 'fromAbove' // 'fromAbove' veya 'fromBelow'
  } = options;

  const group = new THREE.Group();
  const arrowId = generateUniqueId('incomingArrow');

  // 1. Daire (base point - merkez noktada)
  const circle = createFilledCircle(circleRadius, color);
  circle.position.set(0, 0, 0);
  circle.userData = {
    role: 'circle',
    arrowId,
    originalColor: color
  };
  group.add(circle);

  // 2. Üçgen ok başı - Sivri ucu daireye dokunacak şekilde
  // Üçgen: tip (0, height) -> base (-halfBase, 0) to (halfBase, 0)
  // +90° döndürünce tip (-height, 0) olur, yani sola bakar
  // Pozisyon: circleRadius + triangleHeight, böylece tip circleRadius'a değer
  const arrowhead = createTriangleArrowhead(triangleBase, triangleHeight, color);
  arrowhead.position.set(circleRadius + triangleHeight, 0, 0);
  arrowhead.rotation.z = Math.PI / 2; // Sola (daireye) bakacak şekilde döndür
  arrowhead.userData = {
    role: 'arrowhead',
    arrowId,
    originalColor: color
  };
  group.add(arrowhead);

  // 3. Çizgi - Ok başının tabanından dışarı doğru uzanır
  // Çizgi başlangıcı: üçgenin tabanı (circleRadius + triangleHeight)
  // Çizgi bitişi: dışarıya doğru lineLength kadar
  const lineStart = new THREE.Vector3(circleRadius + triangleHeight, 0, 0);
  const lineEnd = new THREE.Vector3(circleRadius + triangleHeight + lineLength, 0, 0);

  const line = createLine(lineStart, lineEnd, color);
  line.userData = {
    role: 'line',
    arrowId,
    originalColor: color
  };
  group.add(line);

  // Grubu konumlandır
  group.position.set(position.x, position.y, 0);

  // Yön belirleme (giden oklarla aynı mantık):
  // 'fromAbove' (yukarıdan gelen): 60 derece (yukarı sağa doğru - yukarıdan geldiğini gösterir)
  // 'fromBelow' (aşağıdan gelen): -60 derece / 300 derece (aşağı sağa doğru - aşağıdan geldiğini gösterir)
  let finalRotation = rotation;
  if (direction === 'fromAbove') {
    finalRotation = 60;  // Yukarı-sağa doğru (yukarıdan geliyor)
  } else if (direction === 'fromBelow') {
    finalRotation = -60; // Aşağı-sağa doğru (aşağıdan geliyor) = 300 derece
  }

  group.rotation.z = finalRotation * Math.PI / 180;

  group.userData = {
    id: arrowId,
    type: 'incomingFloorTransitionArrow',
    isSelectable: true,
    isPreview: isPreview,
    position: { x: position.x, y: position.y },
    rotation: finalRotation,
    direction: direction,
    color: color,
    circleRadius: circleRadius,
    lineLength: lineLength,
    triangleBase: triangleBase,
    triangleHeight: triangleHeight,
    layerName: layerName,
    originalColor: color
  };

  if (target?.add) {
    target.add(group);
  }

  return group;
}

export default {
  createArrow,
  createIncomingArrow,
  createTriangleArrow,
  createCircleMarker,
  createLeader,
  ARROW_DEFAULTS
};
